import React, { useRef, useEffect, useState } from 'react';
import { Send, UserPlus, Sparkles, ChevronRight, Square } from 'lucide-react';
import { SimulationBuilder } from './SimulationBuilder';
import { getStoredSmartWorkspacePatient, isSmartModeActive, SMART_SESSION_NOT_FOUND_MESSAGE } from '../api/smartApi';
// Entity lists come from backend via props (no hardcoded data when Command API enabled)

/** Entity list from backend (backend → DB). All pickers use these; no hardcoded lists. */
export type EntityListFromApi = { id: string; date: string; time?: string; type: string; title?: string | null; summary: string; chips: string[] };

interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (message: string) => void;
  onQuickPrompt: (prompt: string) => void;
  simulationBuilderOpen?: boolean;
  onSimulationGenerate?: (command: string) => void;
  onSimulationBuilderClose?: () => void;
  commandLoading?: boolean;
  onStop?: () => void;
  /** When false, composer is disabled until user selects a patient and has an open conversation for that patient */
  canChat?: boolean;
  /** All entity lists from backend (GET /v1/assessments, /v1/alerts, etc.). No hardcoded data. */
  assessmentsFromApi?: EntityListFromApi[];
  alertsFromApi?: EntityListFromApi[];
  redFlagsFromApi?: EntityListFromApi[];
  exportsFromApi?: EntityListFromApi[];
  simulationsFromApi?: EntityListFromApi[];
  visitsFromApi?: EntityListFromApi[];
  /** Called when user clicks "Select patient" in the disabled state */
  onOpenSelectPatient?: () => void;
  onOpenExportBuilder?: () => void;
  onOpenExportCsvBuilder?: () => void;
  onOpenReviseNoteModal?: () => void;
  onOpenAssessmentTrendBuilder?: () => void;
  onOpenPromptGallery?: () => void;
}

export function Composer({
  value,
  onChange,
  onSend,
  onQuickPrompt,
  simulationBuilderOpen,
  onSimulationGenerate,
  onSimulationBuilderClose,
  commandLoading = false,
  onStop,
  canChat = true,
  assessmentsFromApi = [],
  alertsFromApi = [],
  redFlagsFromApi = [],
  exportsFromApi = [],
  simulationsFromApi = [],
  visitsFromApi = [],
  onOpenSelectPatient,
  onOpenExportBuilder,
  onOpenExportCsvBuilder,
  onOpenReviseNoteModal,
  onOpenAssessmentTrendBuilder,
  onOpenPromptGallery,
}: ComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const smartMode = isSmartModeActive();
  const [smartWorkspacePatient, setSmartWorkspacePatient] = useState(() =>
    smartMode ? getStoredSmartWorkspacePatient() : null,
  );
  const chatReady = canChat || Boolean(smartWorkspacePatient);

  useEffect(() => {
    if (!smartMode || typeof window === 'undefined') return undefined;
    const refreshSmartPatient = () => setSmartWorkspacePatient(getStoredSmartWorkspacePatient());
    refreshSmartPatient();
    window.addEventListener('march-smart-patient-ready', refreshSmartPatient);
    window.addEventListener('storage', refreshSmartPatient);
    return () => {
      window.removeEventListener('march-smart-patient-ready', refreshSmartPatient);
      window.removeEventListener('storage', refreshSmartPatient);
    };
  }, [smartMode]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't handle Enter if SimulationBuilder is open - let it handle Enter
    if (simulationBuilderOpen && e.key === 'Enter') return;

    // Handle plain Enter to send message
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !simulationBuilderOpen) {
      e.preventDefault();
      onSend(value);
    }
  };

  // Auto-resize textarea.
  //
  // Measured on the next frame, not synchronously. Run during the mount commit,
  // `scrollHeight` can be read before layout has given the textarea its final
  // box, and the reading comes back far too large — the box then latches at
  // maxHeight and STAYS there, because every later pass re-measures an element
  // that is already 200px tall. An empty composer rendered 200px of blank input
  // and pushed the transcript off the screen.
  //
  // It surfaced when the chat stopped being wrapped in an extra layout
  // component, which had been supplying the render pass that hid the race. The
  // ordering was always wrong; something else was paying for it.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      const minHeight = 48;
      const maxHeight = 200;
      el.style.height = 'auto';
      const scrollHeight = el.scrollHeight;
      el.style.height = `${Math.min(Math.max(scrollHeight, minHeight), maxHeight)}px`;
      el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    });
    return () => cancelAnimationFrame(frame);
  }, [value]);

  // Publish the composer's real height as --composer-dock-height so the global
  // Job Center launcher can sit ABOVE it instead of on top of the Send button.
  // Measured rather than assumed: the textarea grows from 48px to 200px, so any
  // hardcoded offset is wrong as soon as someone types a long question.
  const dockRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = dockRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const publish = () => {
      document.documentElement.style.setProperty(
        '--composer-dock-height',
        `${Math.round(el.getBoundingClientRect().height)}px`,
      );
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      // Routes without a composer (admin) must not inherit a stale offset.
      document.documentElement.style.removeProperty('--composer-dock-height');
    };
  }, []);

  const handleChange = (newValue: string) => {
    // Typing "/" opens the prompt gallery (replacing the old command palette)
    if (newValue === '/' && onOpenPromptGallery) {
      onOpenPromptGallery();
      onChange('');
      return;
    }
    onChange(newValue);
  };

  if (!chatReady) {
    return (
      <div className="relative mx-auto mb-2 w-full max-w-7xl px-4 py-2.5 sm:px-6">
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface-raised px-4 py-3.5 text-left sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-text-tertiary">
              {smartMode ? 'Patient record required' : 'Patient required'}
            </div>
            <p className="mt-1 text-[14px] leading-snug text-foreground sm:text-[13px]">
              {smartMode
                ? SMART_SESSION_NOT_FOUND_MESSAGE
                : 'Select a patient to open the clinical workspace. Conversations remain scoped to the active patient record.'}
            </p>
          </div>
          {!smartMode && onOpenSelectPatient && (
            <button
              type="button"
              onClick={onOpenSelectPatient}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3.5 py-2.5 text-[14px] font-medium text-primary-foreground shadow-sm shadow-black/20 transition-colors hover:brightness-105 sm:w-auto sm:shrink-0 sm:py-2 sm:text-[13px]"
            >
              <UserPlus className="w-4 h-4" />
              Select patient
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={dockRef} className="relative mx-auto w-full max-w-7xl px-4 py-2 sm:px-6 sm:py-2.5">
      {/* Simulation Builder (opened from Prompt Gallery) */}
      {simulationBuilderOpen && onSimulationGenerate && onSimulationBuilderClose && (
        <SimulationBuilder
          onGenerate={onSimulationGenerate}
          onClose={onSimulationBuilderClose}
        />
      )}

      <div className="relative rounded-lg border border-border bg-surface-raised shadow-sm focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/30">
        {commandLoading && (
          <div className="border-b border-border/70 px-3 py-1.5 text-[11px] font-medium text-text-tertiary sm:px-3.5">
            Processing request…
          </div>
        )}
        <div className="flex items-end gap-1.5 px-2.5 py-2 sm:gap-2 sm:px-3">
          <textarea
            data-testid="composer-input"
            ref={textareaRef}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={smartWorkspacePatient ? `Ask about ${smartWorkspacePatient.name}` : 'Clinical question or command (e.g. /summary)'}
            aria-label="Message input"
            className="min-w-0 flex-1 resize-none border-0 bg-transparent py-1 text-[15px] leading-[1.55] text-foreground placeholder:text-text-tertiary/70 focus:outline-hidden focus:ring-0 sm:text-[14px]"
            rows={1}
            style={{ 
              minHeight: '48px', 
              maxHeight: '200px',
              overflowY: 'auto',
              lineHeight: '1.6'
            }}
          />
          <div className="flex items-center gap-1 shrink-0">
            {onOpenPromptGallery && (
              <button
                type="button"
                data-testid="prompts-button"
                onClick={onOpenPromptGallery}
                className="rounded-md p-2 text-text-tertiary transition-colors hover:bg-surface-hover hover:text-primary sm:p-2"
                title="Prompt library"
                aria-label="Suggested prompts"
              >
                <Sparkles className="h-[18px] w-[18px]" />
              </button>
            )}
            <button
              type="button"
              onClick={onStop}
              disabled={!commandLoading || !onStop}
              className={`rounded-md p-2 transition-colors sm:p-2 ${
                commandLoading && onStop
                  ? 'bg-destructive/15 text-destructive hover:bg-destructive/25'
                  : 'cursor-not-allowed bg-surface-sunken text-text-tertiary'
              }`}
              title={commandLoading ? 'Stop current request' : 'No active request'}
              aria-label="Stop current request"
            >
              <Square className="h-[16px] w-[16px]" strokeWidth={2.2} />
            </button>
            <button
              onClick={() => onSend(value)}
              disabled={!value.trim() || commandLoading}
              className={`rounded-md p-2 transition-colors sm:p-2 ${
                value.trim() && !commandLoading
                  ? 'bg-primary text-primary-foreground hover:brightness-105'
                  : 'cursor-not-allowed bg-surface-sunken text-text-tertiary'
              }`}
              title={!value.trim() ? 'Enter message to send' : 'Send (Enter)'}
              aria-label="Send message"
            >
              <Send className="h-[18px] w-[18px]" strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
